import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Input, Button, Card, Typography, Space, message, Alert } from 'antd'
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { api } from '@services/api'

const { Title, Text } = Typography

interface LoginFormData {
  username: string
  password: string
}

interface MfaFormData {
  otp_code: string
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [mfaStep, setMfaStep] = useState<'password' | 'setup' | 'verify'>('password')
  const [mfaToken, setMfaToken] = useState<string>('')
  const [mfaQrUri, setMfaQrUri] = useState<string>('')
  const navigate = useNavigate()

  const onFinish = async (values: LoginFormData) => {
    // 防止重复提交
    if (loading) {
      console.log('⚠️ 正在登录中，忽略重复提交')
      return
    }
    
    setLoading(true)
    
    try {
      console.log('🔐 开始登录:', values.username)
      const response = await api.auth.login(values)
      
      // 详细调试信息
      console.log('✅ 登录响应:', response)
      console.log('📝 access_token:', response.access_token)
      console.log('📝 token字段:', response.token)
      console.log('📝 用户信息:', response.user)
      
      if (response.mfa_required) {
        if (!response.mfa_token) throw new Error('MFA会话创建失败')
        setMfaToken(response.mfa_token)
        if (response.mfa_setup_required) {
          setMfaStep('setup')
          setMfaQrUri(response.mfa_qr_uri || '')
          message.info('首次登录需先配置MFA，请扫码并输入验证码')
        } else {
          setMfaStep('verify')
          message.info('请输入MFA安全码完成登录')
        }
        return
      }
      const token = response.access_token || response.token
      if (!token) throw new Error('服务器未返回有效的访问令牌')
      
      // 清除旧数据并保存新token
      localStorage.clear()
      localStorage.setItem('token', token)
      if (response.user) {
        localStorage.setItem('user_info', JSON.stringify(response.user))
      }
      
      // 验证token是否正确保存
      const savedToken = localStorage.getItem('token')
      console.log('💾 Token保存验证:', {
        saved: !!savedToken,
        length: savedToken?.length || 0,
        matches: savedToken === token
      })
      
      message.success('登录成功，正在跳转...')
      
      // 立即跳转，不用延迟
      console.log('🚀 跳转到dashboard')
      navigate('/dashboard', { replace: true })
      
    } catch (error: any) {
      console.error('❌ 登录错误:', error)
      const errorMessage = error?.response?.data?.detail || 
                          error?.message || 
                          '登录失败，请检查用户名和密码'
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const finishLogin = (token: string, user: any) => {
    // Reuse existing behavior: clear old auth state then persist new token.
    localStorage.clear()
    localStorage.setItem('token', token)
    if (user) localStorage.setItem('user_info', JSON.stringify(user))
    message.success('登录成功，正在跳转...')
    navigate('/dashboard', { replace: true })
  }

  const onMfaSetupFinish = async (values: MfaFormData) => {
    if (loading) return
    setLoading(true)
    try {
      const response = await api.auth.verifyMfaSetup({ mfa_token: mfaToken, otp_code: values.otp_code })
      const token = response.access_token || response.token
      if (!token) throw new Error('登录令牌缺失')
      finishLogin(token, response.user)
    } catch (error: any) {
      message.error(error?.response?.data?.detail || error?.message || 'MFA配置验证失败')
    } finally {
      setLoading(false)
    }
  }

  const onMfaVerifyFinish = async (values: MfaFormData) => {
    if (loading) return
    setLoading(true)
    try {
      const response = await api.auth.verifyMfaLogin({ mfa_token: mfaToken, otp_code: values.otp_code })
      const token = response.access_token || response.token
      if (!token) throw new Error('登录令牌缺失')
      finishLogin(token, response.user)
    } catch (error: any) {
      message.error(error?.response?.data?.detail || error?.message || 'MFA验证失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* 背景装饰元素 */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '80px',
        height: '80px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '50%',
        animation: 'float 3s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        top: '70%',
        right: '15%',
        width: '60px',
        height: '60px',
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '50%',
        animation: 'float 2.5s ease-in-out infinite reverse'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '20%',
        left: '20%',
        width: '100px',
        height: '100px',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '50%',
        animation: 'float 3.5s ease-in-out infinite'
      }} />

      <Card
        style={{
          width: 420,
          maxWidth: '90vw',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
          borderRadius: 16,
          border: 'none',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          zIndex: 1
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center', padding: '20px 0 10px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #1890ff, #52c41a)',
              borderRadius: '50%',
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '36px',
              boxShadow: '0 8px 20px rgba(24, 144, 255, 0.3)'
            }}>
              🚀
            </div>
            <Title level={2} style={{ 
              margin: 0, 
              background: 'linear-gradient(135deg, #1890ff, #52c41a)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              TradingBridge Pro
            </Title>
            <Text style={{ 
              color: '#666', 
              fontSize: '16px',
              display: 'block',
              marginTop: '8px'
            }}>
              专业的合约交易自动化系统
            </Text>
            <div style={{
              width: '60px',
              height: '3px',
              background: 'linear-gradient(135deg, #1890ff, #52c41a)',
              margin: '16px auto',
              borderRadius: '2px'
            }} />
          </div>

          {mfaStep === 'password' && (
            <Form
              name="login"
              onFinish={onFinish}
              autoComplete="off"
              size="large"
              style={{ padding: '0 10px' }}
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                  placeholder="请输入用户名"
                  autoComplete="username"
                  className="login-input"
                  style={{
                    borderRadius: 12,
                    border: '2px solid #f0f0f0',
                    padding: '12px 16px',
                    fontSize: '16px',
                    transition: 'all 0.3s ease'
                  }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                  placeholder="请输入密码"
                  autoComplete="current-password"
                  className="login-input"
                  style={{
                    borderRadius: 12,
                    border: '2px solid #f0f0f0',
                    padding: '12px 16px',
                    fontSize: '16px',
                    transition: 'all 0.3s ease'
                  }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 20 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  className="login-submit-button"
                  style={{
                    width: '100%',
                    height: 50,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #1890ff, #52c41a)',
                    border: 'none',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    boxShadow: '0 8px 20px rgba(24, 144, 255, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {loading ? '登录中...' : '立即登录'}
                </Button>
              </Form.Item>
            </Form>
          )}

          {(mfaStep === 'setup' || mfaStep === 'verify') && (
            <Form
              name="mfa"
              onFinish={mfaStep === 'setup' ? onMfaSetupFinish : onMfaVerifyFinish}
              autoComplete="off"
              size="large"
              style={{ padding: '0 10px' }}
            >
              <Alert
                showIcon
                type="info"
                style={{ marginBottom: 16 }}
                message={mfaStep === 'setup' ? '首次登录：请先完成MFA绑定' : '请输入MFA安全码'}
                description={
                  mfaStep === 'setup'
                    ? '请用 Google Authenticator / Microsoft Authenticator 扫描下方链接后输入6位验证码。'
                    : '每次登录都需要输入MFA动态码。'
                }
              />
              {mfaStep === 'setup' && mfaQrUri && (
                <>
                  <Form.Item>
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(mfaQrUri)}`}
                        alt="MFA QR Code"
                        style={{ width: 220, height: 220, borderRadius: 8, border: '1px solid #f0f0f0' }}
                      />
                    </div>
                  </Form.Item>
                  <Form.Item>
                    <Input
                      prefix={<SafetyCertificateOutlined style={{ color: '#1890ff' }} />}
                      value={mfaQrUri}
                      readOnly
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item
                name="otp_code"
                rules={[
                  { required: true, message: '请输入6位MFA验证码' },
                  { pattern: /^\d{6}$/, message: '验证码格式应为6位数字' },
                ]}
              >
                <Input
                  maxLength={6}
                  placeholder="输入6位MFA验证码"
                  prefix={<SafetyCertificateOutlined style={{ color: '#1890ff' }} />}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 20 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{
                    width: '100%',
                    height: 50,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #1890ff, #52c41a)',
                    border: 'none',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    boxShadow: '0 8px 20px rgba(24, 144, 255, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {loading ? '验证中...' : '验证并登录'}
                </Button>
              </Form.Item>
            </Form>
          )}

          {/* Production: do not show test credentials */}
        </Space>
      </Card>
    </div>
  )
}

export default Login

